import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { computed, reactive, watch } from 'vue';
import {
    BaseProductItem,
    BasketCheckoutModel,
    BasketCheckoutModelSummary,
    BasketDeliveryAddress,
    BasketInvoiceAddress,
    DeliveryTimesForCustomerDelivery,
    DeliveryOptionModel,
    EarliestCustomerDeliveryDates,
    PickUpPointsForDelivery,
    PickUpTimesForCustomerDelivery,
    SetDeliveryTimeRequest,
    SetCustomerDeliveryCommentRequest,
    B2CClientService,
    AddInvoiceAddressToBasketRequest,
    DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery,
    CheckoutDeliverableType,
    ParcelShopModel,
    AddRedeemCodeRequest,
    PaymentInitializeResult,
} from '@/b2c/shared/api/server-apis';
import useBasket from '@/b2c/basket/useBasket';
import utilitiesService from '@/shared/utilities.service';
import { LinkModel } from '@/types/B2CcontentServerContract';
import { maybeSoftRedirect, softRedirect } from '@/core/navigation/redirect.service';
import siteContextStore from '@/shared/content/site-context.store';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';

export interface ICheckoutState {
    step: number;
    basket: BasketCheckoutModel;
    deliveryInfo: DeliverableItemsDeliveryInfo;
    itemIdsOutOfStock: number[];
    receiptPageLink: LinkModel;
}

export interface DeliverableItemsDeliveryInfo {
    [id: number]: EarliestCustomerDeliveryDates,
}

const state: ICheckoutState = reactive({
    step: 0,
    basket: {} as BasketCheckoutModel,
    deliveryInfo: {} as DeliverableItemsDeliveryInfo,
    selectedPaymentOption: null,
    itemIdsOutOfStock: [] as number[],
    showChooseSalesPersonDialog: false,
    receiptPageLink: {} as LinkModel,
});

export function useCheckout() {
    const { basketId, get: refreshBasket } = useBasket();
    const stepQuery = useRouteQuery<number>(QueryKeys.CheckoutStep);

    const step = computed<number>(() => {
        return state.step;
    });

    const isAddressStep = computed<boolean>(() => {
        return state.step === 0;
    });

    const isDeliverableStep = computed<boolean>(() => {
        return state.step !== 0;
    });

    const deliverableCount = computed<number>(() => {
        return state.basket.deliverables?.length ?? 0;
    });

    const isLastStep = computed<boolean>(() => {
        return state.basket.deliverables?.length === state.step ?? false;
    });

    const items = computed<BaseProductItem[]>(() => {
        return state.basket.items ?? [] as BaseProductItem[];
    });

    const summary = computed<BasketCheckoutModelSummary>(() => {
        return state.basket.summary ?? {} as BasketCheckoutModelSummary;
    });

    const deliveryInfo = computed<DeliverableItemsDeliveryInfo>(() => {
        return state.deliveryInfo ?? {} as DeliverableItemsDeliveryInfo;
    });

    const invoiceAddress = computed<BasketInvoiceAddress | undefined>(() => {
        return state.basket.invoiceAddress;
    });

    const basket = computed<BasketCheckoutModel>(() => {
        return state.basket ?? {} as BasketCheckoutModel;
    });

    const itemIdsOutOfStock = computed<number[]>(() => {
        return state.itemIdsOutOfStock;
    });

    const receiptPageLink = computed<LinkModel>(() => {
        return state.receiptPageLink;
    });

    const hasSufficientPayments = computed<boolean>(() => {
        return state.basket.hasSufficientPayments;
    });

    const isClickAndCollectOnly = computed<boolean>(() => {
        return state.basket.deliverables.length === 1 && state.basket.deliverables[0].type === CheckoutDeliverableType.ClickAndCollect;
    });

    const init = async(receiptPageLink: LinkModel): Promise<void> => {
        state.receiptPageLink = receiptPageLink;
        const response = await B2CClientService.getBasketForCheckOut(basketId.value).then(utilitiesService.unwrapModel);
        if (response) {
            basketTrackingService.beginCheckout(response);
        }

        if (response?.items.length === 0) {
            softRedirect(siteContextStore.settings.basketPageUrl);
        }
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setDeliveryAddress = async(deliverableId: number, request: BasketDeliveryAddress): Promise<void> => {
        const response = await B2CClientService
            .setDeliveryAddress(basketId.value, deliverableId, request)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getPickUpErrorMessageId = (id: number) => `pickUpErrorMessageId-${id}`;

    const setDeliveryTime = async(deliverableId: number, request: SetDeliveryTimeRequest, messageId?: string): Promise<void> => {
        const response = await B2CClientService
            .setDeliveryTime(basketId.value, deliverableId, request, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setCommentForHomeDelivery = async(deliverableId: number, request: SetCustomerDeliveryCommentRequest, messageId?: string): Promise<void> => {
        const response = await B2CClientService
            .setCommentForHomeDelivery(basketId.value, deliverableId, request, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const addInvoiceAddress = async(request: AddInvoiceAddressToBasketRequest): Promise<void> => {
        const response = await B2CClientService
            .addInvoiceAddress(basketId.value, request)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setDeliveryInformationForItems = async(deliverableId: number): Promise<void> => {
        const response = await B2CClientService
            .getEarliestDeliveryDateForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        if (response) {
            state.deliveryInfo[deliverableId] = response;
        }
    };

    const clearDeliveryInformationForItems = (deliverableId: number): void => {
        if (state.deliveryInfo[deliverableId]) {
            delete state.deliveryInfo[deliverableId];
        }
    };

    const getDeliveryTimesForCustomerDelivery = async(deliverableId: number): Promise<DeliveryTimesForCustomerDelivery> => {
        const response = await B2CClientService
            .getDeliveryTimesForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryTimesForCustomerDelivery;
    };

    const addDeliveryOption = async(deliverableId: number, itemId: string): Promise<void> => {
        const response = await B2CClientService
            .addDeliveryOption(basketId.value, deliverableId, itemId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeDeliveryOption = async(deliverableId: number, itemId: string): Promise<void> => {
        const response = await B2CClientService
            .removeDeliveryOption(basketId.value, deliverableId, itemId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getDeliveryOptions = async(countryCode: string): Promise<(DeliveryOptionModel|undefined)[]> => {
        const response = await B2CClientService
            .getDeliveryOptions(countryCode, basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? [] as DeliveryOptionModel[];
    };

    const getDeliveryOptionsForClickAndCollect = async(): Promise<(DeliveryOptionModel|undefined)[]> => {
        const response = await B2CClientService
            .getDeliveryOptionsForClickAndCollect(basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? [] as DeliveryOptionModel[];
    };

    const getPickUpPointsForDelivery = async(deliverableId: number): Promise<PickUpPointsForDelivery> => {
        const response = await B2CClientService
            .getPickUpPointsForDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpPointsForDelivery;
    };

    const getPickUpTimesForCustomerDelivery = async(deliverableId: number, hubId: string): Promise<PickUpTimesForCustomerDelivery> => {
        const response = await B2CClientService
            .getPickUpTimesForCustomerDelivery(basketId.value, deliverableId, hubId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpTimesForCustomerDelivery;
    };

    const changeClickAndCollectStoreFromCheckOut = async(storeId: string): Promise<void> => {
        const response = await B2CClientService
            .changeClickAndCollectStoreFromCheckOut(basketId.value, storeId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const splitDelivery = async(deliveryId: number, itemIds: number[]): Promise<void> => {
        const response = await B2CClientService
            .splitDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDelivery = async(deliveryId: number): Promise<void> => {
        const response = await B2CClientService
            .removeSplitDelivery(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDeliveries = async(deliveryId: number): Promise<BasketCheckoutModel> => {
        const response = await B2CClientService
            .removeSplitDeliveries(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
        return state.basket;
    };

    const getDeliveryDateAndFreightPriceForSplitItems = async(deliveryId: number, itemIds: number[]): Promise<DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery> => {
        const response = await B2CClientService
            .getDeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery;
    };

    const getNearestParcelShopsForDelivery = async(deliveryId: number, amount: number): Promise<ParcelShopModel[]> => {
        const response = await B2CClientService
            .getNearestParcelShopsForDelivery(basketId.value, deliveryId, amount)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as ParcelShopModel[];
    };

    const getParcelShopsForZipCode = async(postalCode: string | null): Promise<ParcelShopModel[]> => {
        const response = await B2CClientService
            .getParcelShopsForZipCode(postalCode, basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as ParcelShopModel[];
    };

    const setPickAndPackToParcelShopDelivery = async(parcelShop: ParcelShopModel): Promise<void> => {
        const response = await B2CClientService
            .setPickAndPackToParcelShopDelivery(basketId.value, parcelShop)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setPickAndPackToHomeDelivery = async(): Promise<void> => {
        const response = await B2CClientService
            .setPickAndPackToHomeDelivery(basketId.value)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const initializePayment = async(): Promise<PaymentInitializeResult> => {
        basketTrackingService.addShippingInfo(basket.value);
        const response = await B2CClientService
            .initializePayment(basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PaymentInitializeResult;
    };

    const submitOrderWithSufficientPaymentsInBasket = async(subscribeNewsletter: boolean): Promise<void> => {
        const response = await B2CClientService
            .submitOrderWithSufficientPaymentsInBasket(basketId.value, { subscribeNewsletter })
            .then(utilitiesService.unwrapModel);
        if (response?.orderNumber) {
            await redirectToReceipt(response.orderNumber);
        }
    };

    const addGiftCardPayment = async(request: AddRedeemCodeRequest, messageId?: string): Promise<void> => {
        const response = await B2CClientService.addGiftCardPayment(basketId.value, request, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeGiftCardPayment = async(code: string, messageId?: string): Promise<void> => {
        const response = await B2CClientService.removeGiftCardPaymentFromCheckOut(basketId.value, code, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getTermsAndConditions = async(): Promise<string> => {
        const response = await B2CClientService.getById(basket.value.termsId)
            .then(utilitiesService.unwrapModel);

        return response?.description ?? '';
    };

    const setItemIdsOutOfStock = (items?: number[]): void => {
        state.itemIdsOutOfStock = items?.length ? items : [];
    };

    const clearItemIdsOutOfStock = (): void => {
        state.itemIdsOutOfStock.length = 0;
    };

    const updateStep = (step: number): void => {
        stepQuery.value = step;
    };

    const gotoNextStep = (): void => {
        basketTrackingService.addShippingInfo(basket.value);

        if (!isLastStep.value) {
            stepQuery.value = state.step + 1;
        }
    };

    const redirectToReceipt = async(orderNumber: string) => {
        const redirectLinkWithFallback = receiptPageLink.value ?? { url: siteContextStore.settings.frontPageUrl, spaLink: false };
        const { url: path, spaLink } = redirectLinkWithFallback;
        const url = `${path}?${QueryKeys.Id}=${orderNumber}`;
        maybeSoftRedirect({
            url,
            spaLink,
        });
        await refreshBasket();
    };

    watch(stepQuery, step => {
        state.step = step;
    }, { immediate: true });

    return {
        items,
        summary,
        invoiceAddress,
        deliveryInfo,
        deliverableCount,
        basket,
        step,
        isAddressStep,
        isDeliverableStep,
        isLastStep,
        itemIdsOutOfStock,
        receiptPageLink,
        hasSufficientPayments,
        isClickAndCollectOnly,
        init,
        setDeliveryAddress,
        addInvoiceAddress,
        setDeliveryInformationForItems,
        clearDeliveryInformationForItems,
        getPickUpErrorMessageId,
        setDeliveryTime,
        setCommentForHomeDelivery,
        getDeliveryTimesForCustomerDelivery,
        getDeliveryOptions,
        getDeliveryOptionsForClickAndCollect,
        addDeliveryOption,
        removeDeliveryOption,
        getPickUpPointsForDelivery,
        getPickUpTimesForCustomerDelivery,
        changeClickAndCollectStoreFromCheckOut,
        setItemIdsOutOfStock,
        addGiftCardPayment,
        removeGiftCardPayment,
        clearItemIdsOutOfStock,
        updateStep,
        gotoNextStep,
        splitDelivery,
        removeSplitDelivery,
        removeSplitDeliveries,
        getDeliveryDateAndFreightPriceForSplitItems,
        getNearestParcelShopsForDelivery,
        getParcelShopsForZipCode,
        setPickAndPackToParcelShopDelivery,
        setPickAndPackToHomeDelivery,
        initializePayment,
        submitOrderWithSufficientPaymentsInBasket,
        redirectToReceipt,
        getTermsAndConditions,
    };
}
